<script setup>
import { ref, inject, onMounted, watch } from 'vue';
import {notificationType, showToast} from '../utility/toastNotificationUtility';

const model = defineModel({
  type: Object,
  default: () => ({})
});

const personalInfo = inject('personalInfo');

const emailCode = ref('');
const phoneCode = ref('');
const emailVerified = ref(model.value.emailVerified || false);
const phoneVerified = ref(model.value.phoneVerified || false);
const emailSending = ref(false);
const phoneSending = ref(false);
const emailVerifying = ref(false);
const phoneVerifying = ref(false);
const emailSent = ref(false);
const phoneSent = ref(false);
const emailCooldown = ref(0);
const phoneCooldown = ref(0);

// Reset flags when verification status changes (e.g. from parent component)
watch(() => model.value.emailVerified, (newVal) => {
  emailVerified.value = newVal;
  if (newVal === false) {
    emailSent.value = false;
    emailCode.value = '';
  }
}, { immediate: true });

watch(() => model.value.phoneVerified, (newVal) => {
  phoneVerified.value = newVal;
  if (newVal === false) {
    phoneSent.value = false;
    phoneCode.value = '';
  }
}, { immediate: true });

const sendEmailOtp = async () => {
  if (emailSending.value || emailCooldown.value > 0) return;
  
  const email = personalInfo?.email;
  if (!email) {
    showToast(
        { title: 'Error',
          body: 'Please enter your email address first.',
          type: notificationType.ERROR });
    return;
  }

  emailSending.value = true;
  try {
    const res = await fetch('/api/independent-driver-signup/send-email-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    if (res.ok) {
      emailSent.value = true;
      showToast({ title: 'Code Sent', body: 'A verification code has been sent to your email.', type: notificationType.SUCCESS });
      startEmailCooldown();
    } else {
      const errorText = await res.text();
      showToast({ title: 'Error', body: errorText || 'Failed to send verification code.', type: notificationType.ERROR });
    }
  } catch (e) {
    showToast({ title: 'Error', body: 'Network error. Please try again.', type: notificationType.ERROR });
  } finally {
    emailSending.value = false;
  }
};

const sendPhoneOtp = async () => {
  if (phoneSending.value || phoneCooldown.value > 0) return;
  
  const phone = `${personalInfo?.countryCode}${personalInfo?.phoneNumber}`;
  if (!personalInfo?.phoneNumber) {
    showToast({ title: 'Error', body: 'Please enter your phone number first.', type: 'error' });
    return;
  }

  phoneSending.value = true;
  try {
    const res = await fetch('/api/independent-driver-signup/send-phone-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phoneNumber: phone })
    });

    if (res.ok) {
      phoneSent.value = true;
      showToast({ title: 'Code Sent', body: 'A verification code has been sent to your phone.', type: 'success' });
      startPhoneCooldown();
    } else {
      const errorText = await res.text();
      showToast({ title: 'Error', body: errorText || 'Failed to send verification code.', type: 'error' });
    }
  } catch (e) {
    showToast({ title: 'Error', body: 'Network error. Please try again.', type: 'error' });
  } finally {
    phoneSending.value = false;
  }
};

const verifyEmailOtp = async () => {
  if (emailVerifying.value || !emailCode.value) return;

  emailVerifying.value = true;
  try {
    const res = await fetch('/api/independent-driver-signup/verify-email-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: personalInfo?.email, code: emailCode.value })
    });

    if (res.ok) {
      emailVerified.value = true;
      model.value.emailVerified = true;
      showToast({ title: 'Verified', body: 'Email verified successfully!', type: notificationType.SUCCESS });
    } else {
      const errorText = await res.text();
      showToast({ title: 'Invalid Code', body: errorText || 'The code you entered is incorrect.', type: notificationType.ERROR });
    }
  } catch (e) {
    showToast({ title: 'Error', body: 'Network error. Please try again.', type: notificationType.ERROR });
  } finally {
    emailVerifying.value = false;
  }
};

const verifyPhoneOtp = async () => {
  if (phoneVerifying.value || !phoneCode.value) return;

  phoneVerifying.value = true;
  try {
    const phone = `${personalInfo?.countryCode}${personalInfo?.phoneNumber}`;
    const res = await fetch('/api/independent-driver-signup/verify-phone-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phoneNumber: phone, code: phoneCode.value })
    });

    if (res.ok) {
      phoneVerified.value = true;
      model.value.phoneVerified = true;
      showToast({ title: 'Verified', body: 'Phone number verified successfully!', type: notificationType.SUCCESS });
    } else {
      const errorText = await res.text();
      showToast({ title: 'Invalid Code', body: errorText || 'The code you entered is incorrect.', type: notificationType.ERROR });
    }
  } catch (e) {
    showToast({ title: 'Error', body: 'Network error. Please try again.', type: notificationType.ERROR });
  } finally {
    phoneVerifying.value = false;
  }
};

const startEmailCooldown = () => {
  emailCooldown.value = 60;
  const interval = setInterval(() => {
    emailCooldown.value--;
    if (emailCooldown.value <= 0) clearInterval(interval);
  }, 1000);
};

const startPhoneCooldown = () => {
  phoneCooldown.value = 60;
  const interval = setInterval(() => {
    phoneCooldown.value--;
    if (phoneCooldown.value <= 0) clearInterval(interval);
  }, 1000);
};

onMounted(() => {
  emailVerified.value = model.value.emailVerified || false;
  phoneVerified.value = model.value.phoneVerified || false;
});
</script>

<template>
  <div class="signup-step signup-step-verification">
    <div class="row g-4">
      <div class="col-12">
        <h5 class="text-primary mb-3">Verify Your Contact Information</h5>
        <p class="text-muted">Please verify your email address and phone number to continue with registration.</p>
      </div>

      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header bg-info">
            <h6 class="mb-0">
              <i class="fa-solid fa-envelope me-2"></i>Email Verification
              <span v-if="emailVerified" class="badge bg-success ms-2"><i class="fa-solid fa-check"></i> Verified</span>
            </h6>
          </div>
          <div class="card-body">
            <p class="bold  fs-6">{{ personalInfo?.email || 'No email provided' }}</p>
            
            <div v-if="!emailVerified">
              <div v-if="!emailSent" class="d-grid">
                <button type="button" class="btn btn-outline-primary" @click="sendEmailOtp" 
                        :disabled="emailSending || emailCooldown > 0">
                  <span v-if="emailSending" class="spinner-border spinner-border-sm me-2"></span>
                  {{ emailCooldown > 0 ? `Resend in ${emailCooldown}s` : 'Send Verification Code' }}
                </button>
              </div>

              <div v-else>
                <label class="form-label">Enter the 6-digit code sent to your email:</label>
                <div class="input-group mb-3">
                  <input type="text" class="form-control" v-model="emailCode" 
                         placeholder="Enter code" maxlength="6" pattern="[0-9]*">
                  <button type="button" class="btn btn-primary" @click="verifyEmailOtp" 
                          :disabled="emailVerifying || emailCode.length < 4">
                    <span v-if="emailVerifying" class="spinner-border spinner-border-sm me-2"></span>
                    Verify
                  </button>
                </div>
                <button type="button" class="btn btn-link btn-sm p-0" @click="sendEmailOtp" 
                        :disabled="emailSending || emailCooldown > 0">
                  {{ emailCooldown > 0 ? `Resend in ${emailCooldown}s` : 'Resend Code' }}
                </button>
              </div>
            </div>

            <div v-else class="text-center py-3">
              <i class="fa-solid fa-circle-check text-success fa-3x"></i>
              <p class="mt-2 mb-0 text-success">Email verified!</p>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header bg-info">
            <h6 class="mb-0">
              <i class="fa-solid fa-phone me-2"></i>Phone Verification
              <span v-if="phoneVerified" class="badge bg-success ms-2"><i class="fa-solid fa-check"></i> Verified</span>
            </h6>
          </div>
          <div class="card-body">
            <p class="bold fs-6">{{ personalInfo?.countryCode }}{{ personalInfo?.phoneNumber || 'No phone provided' }}</p>
            
            <div v-if="!phoneVerified">
              <div v-if="!phoneSent" class="d-grid">
                <button type="button" class="btn btn-outline-primary" @click="sendPhoneOtp" 
                        :disabled="phoneSending || phoneCooldown > 0">
                  <span v-if="phoneSending" class="spinner-border spinner-border-sm me-2"></span>
                  {{ phoneCooldown > 0 ? `Resend in ${phoneCooldown}s` : 'Send Verification Code' }}
                </button>
              </div>

              <div v-else>
                <label class="form-label">Enter the 6-digit code sent to your phone:</label>
                <div class="input-group mb-3">
                  <input type="text" class="form-control" v-model="phoneCode" 
                         placeholder="Enter code" maxlength="6" pattern="[0-9]*">
                  <button type="button" class="btn btn-primary" @click="verifyPhoneOtp" 
                          :disabled="phoneVerifying || phoneCode.length < 4">
                    <span v-if="phoneVerifying" class="spinner-border spinner-border-sm me-2"></span>
                    Verify
                  </button>
                </div>
                <button type="button" class="btn btn-link btn-sm p-0" @click="sendPhoneOtp" 
                        :disabled="phoneSending || phoneCooldown > 0">
                  {{ phoneCooldown > 0 ? `Resend in ${phoneCooldown}s` : 'Resend Code' }}
                </button>
              </div>
            </div>

            <div v-else class="text-center py-3">
              <i class="fa-solid fa-circle-check text-success fa-3x"></i>
              <p class="mt-2 mb-0 text-success">Phone verified!</p>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12" v-if="!emailVerified || !phoneVerified">
        <div class="alert alert-warning">
          <i class="fa-solid fa-exclamation-triangle me-2"></i>
          Both email and phone number must be verified before you can proceed to the next step.
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
